import * as React from "react";
import { NavLink, useLocation } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";

/**
 * SIDE NAVIGATION - shared by both dashboards.
 * =============================================
 * Driven entirely by an `items` config so the teachers panel and the students
 * panel render the same markup and differ only in their route list. Colors are
 * never passed in: every accent below is a semantic token (bg-primary-soft,
 * text-primary), which resolves to teal under .admin and violet under
 * .dashboard via src/styles/base/_tokens.scss.
 *
 * Icons inherit `currentColor`, so the row's text color drives the icon too -
 * that is what replaced the ~16 useMatch() calls whose only job was to pick an
 * icon color. Active state now comes from NavLink's own `isActive`.
 *
 * Item shapes:
 *   { to, label, icon, end }              -> link
 *   { onClick, label, icon, tone }        -> action (renders a real <button>)
 *   { label, icon, children: [...] }      -> collapsible group
 *
 * `collapsed` is owned by the caller: the desktop sidebars persist it, the
 * mobile drawer hardcodes false (a drawer is never icons-only).
 */

// Every row - link, action button, group trigger - renders through this exact
// string, so they cannot drift apart. `text-start` is load-bearing: <button>
// defaults to text-align:center and Tailwind's preflight does not reset it,
// unlike the <a> rows.
const ROW_BASE =
  "group relative flex items-center w-full min-w-0 rounded-soft h-[4rem] " +
  "text-base text-start transition-colors duration-150 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

// Every state carries its own hover. ROW_ACTIVE used to omit one, leaving the
// current route as the single row that stayed dead under the cursor.
//
// It hovers *deeper into the accent* rather than toward the sunken grey, so it
// keeps reading as selected. Note primary-subtle is LIGHTER than primary-soft,
// so it is the wrong direction here - hence the alpha step. The darker ink is
// not decoration: primary-on-soft is 4.83:1, and deepening the fill alone would
// drop it under AA, while primary-active holds ~7:1.
const ROW_REST = "text-ink-muted hover:bg-surface-sunken hover:text-ink";
const ROW_ACTIVE =
  "bg-primary-soft text-primary font-medium " +
  "hover:bg-primary/15 hover:text-primary-active";
const ROW_DANGER = "text-danger hover:bg-danger-soft";

const ICON = { fontSize: "2rem" };

/** Inline-start accent bar on the active row. Logical inset keeps it correct in RTL. */
function ActiveMarker() {
  return (
    <span
      aria-hidden="true"
      className="absolute start-0 top-[0.8rem] bottom-[0.8rem] w-[3px] rounded-pill bg-primary"
    />
  );
}

/**
 * Wraps a row in a tooltip, but only while collapsed - otherwise the label is
 * already visible.
 *
 * The <span> is not decoration, do not inline the child back into <Tooltip>.
 * Tooltip clones its child and merges classes with
 * `clsx(other.className, children.props.className)`. NavLink's className is a
 * *function* (that is how isActive works) and clsx ignores functions, so the
 * merge collapsed to "" and overwrote it - the anchor rendered as class="",
 * shrink-wrapped to its icon, and only the icon was hoverable. Group and action
 * rows pass a plain string, so they were never affected, which is what made the
 * bug look like a group-only hover effect.
 *
 * Giving Tooltip a span to clone keeps the merge on a string and leaves the
 * row's own className untouched. The span carries the row's full width so the
 * tooltip's hit area is the whole row, not just the icon.
 */
function CollapsedTip({ collapsed, label, children }) {
  if (!collapsed) return children;
  return (
    <Tooltip title={label} placement="left" arrow enterDelay={200}>
      <span className="flex w-full min-w-0">{children}</span>
    </Tooltip>
  );
}

// Collapsed rows drop the text and keep an aria-hidden icon, so each row sets
// its own aria-label in that state. It cannot be left to the Tooltip: Tooltip
// puts its label on the element it clones, which is now CollapsedTip's span
// rather than the control itself.
function RowContent({ icon: Icon, label, collapsed }) {
  return (
    <>
      <Icon sx={ICON} className="shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </>
  );
}

function NavItem({ item, collapsed }) {
  const layout = collapsed ? "justify-center px-0" : "gap-sm px-sm";

  // Action rows (logout) are buttons, not anchors. The previous markup used
  // <a onClick> with no href, which is not reachable by keyboard at all.
  if (item.onClick) {
    return (
      <li className="min-w-0">
        <CollapsedTip collapsed={collapsed} label={item.label}>
          <button
            type="button"
            onClick={item.onClick}
            aria-label={collapsed ? item.label : undefined}
            className={`${ROW_BASE} ${layout} ${
              item.tone === "danger" ? ROW_DANGER : ROW_REST
            }`}
          >
            <RowContent
              icon={item.icon}
              label={item.label}
              collapsed={collapsed}
            />
          </button>
        </CollapsedTip>
      </li>
    );
  }

  return (
    <li className="min-w-0 flex justify-center">
      <CollapsedTip collapsed={collapsed} label={item.label}>
        <NavLink
          to={item.to}
          end={item.end}
          aria-label={collapsed ? item.label : undefined}
          className={({ isActive }) =>
            `${ROW_BASE} ${layout} ${isActive ? ROW_ACTIVE : ROW_REST}`
          }
        >
          {({ isActive }) => (
            <>
              {isActive && <ActiveMarker />}
              <RowContent
                icon={item.icon}
                label={item.label}
                collapsed={collapsed}
              />
            </>
          )}
        </NavLink>
      </CollapsedTip>
    </li>
  );
}

function NavGroup({ item, collapsed, onRequestExpand }) {
  const { pathname } = useLocation();
  const hasActiveChild = item.children.some((child) =>
    pathname.startsWith(child.to),
  );
  const [open, setOpen] = React.useState(hasActiveChild);

  // Navigating straight to a child (deep link, back button) should reveal it.
  React.useEffect(() => {
    if (hasActiveChild) setOpen(true);
  }, [hasActiveChild]);

  const handleClick = () => {
    // Collapsed groups have nowhere to put children, so borrow the toggle:
    // expand the whole sidebar, then open the group.
    if (collapsed) {
      onRequestExpand?.();
      setOpen(true);
      return;
    }
    setOpen((prev) => !prev);
  };

  const layout = collapsed ? "justify-center px-0" : "gap-sm px-sm";
  const showsActive = hasActiveChild && !open;

  return (
    <li className="min-w-0">
      <CollapsedTip collapsed={collapsed} label={item.label}>
        <button
          type="button"
          onClick={handleClick}
          aria-expanded={open}
          aria-label={collapsed ? item.label : undefined}
          className={`${ROW_BASE} ${layout} ${
            collapsed ? "" : "pe-[2.8rem]"
          } ${showsActive ? ROW_ACTIVE : ROW_REST}`}
        >
          {showsActive && <ActiveMarker />}
          <RowContent
            icon={item.icon}
            label={item.label}
            collapsed={collapsed}
          />

          {!collapsed && (
            <ExpandMoreIcon
              aria-hidden="true"
              sx={{ fontSize: "1.8rem" }}
              className={`pointer-events-none absolute end-sm top-1/2 -translate-y-1/2 transition-transform duration-200 ${
                open ? "-rotate-180" : ""
              }`}
            />
          )}
        </button>
      </CollapsedTip>

      {!collapsed && (
        <div
          className={`overflow-hidden transition-all duration-200 ${
            open ? "max-h-[20rem] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <ul className="flex flex-col gap-[0.2rem] pt-[0.4rem] ps-lg">
            {item.children.map((child) => (
              <NavItem key={child.to} item={child} collapsed={false} />
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

function renderItems(items, collapsed, onRequestExpand) {
  return items.map((item, index) =>
    item.children ? (
      <NavGroup
        key={item.label}
        item={item}
        collapsed={collapsed}
        onRequestExpand={onRequestExpand}
      />
    ) : (
      <NavItem
        key={item.to ?? `${item.label}-${index}`}
        item={item}
        collapsed={collapsed}
      />
    ),
  );
}

/**
 * @param {Array}  items        primary navigation
 * @param {Array}  footerItems  secondary actions (rules, logout) - visually separated
 * @param {Node}   brand        logo / home link, rendered in the header
 * @param {Node}   footer       optional block below footerItems (e.g. the admin profile card)
 * @param {bool}   collapsed    icons-only mode
 * @param {func}   onToggle     omit to hide the collapse control (mobile drawer)
 * @param {string} ariaLabel    names the <nav> for screen readers
 */
export function SideNav({
  items = [],
  footerItems = [],
  brand,
  footer,
  collapsed = false,
  onToggle,
  ariaLabel,
}) {
  const navId = React.useId();
  const expand = React.useCallback(() => onToggle?.(false), [onToggle]);

  return (
    <div className="flex h-full flex-col gap-lg overflow-hidden py-xl">
      <div
        className={`flex h-[4rem] items-center gap-sm px-sm ${
          collapsed ? "justify-center" : "justify-between"
        }`}
      >
        {brand}
        {onToggle && (
          <Tooltip
            title={collapsed ? "باز کردن منو" : "بستن منو"}
            placement="left"
            arrow
          >
            <button
              type="button"
              onClick={() => onToggle(!collapsed)}
              aria-expanded={!collapsed}
              aria-controls={navId}
              aria-label={collapsed ? "باز کردن منو" : "بستن منو"}
              className="flex h-[3.2rem] w-[3.2rem] shrink-0 items-center justify-center rounded-soft text-ink-subtle transition-colors duration-150 hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <MenuOpenIcon
                sx={{ fontSize: "2rem" }}
                className={`transition-transform duration-200 ${
                  collapsed ? "rotate-180" : ""
                }`}
              />
            </button>
          </Tooltip>
        )}
      </div>

      <nav
        id={navId}
        aria-label={ariaLabel}
        className="flex-1 overflow-y-auto overflow-x-hidden px-sm"
      >
        <ul className="flex flex-col gap-[1rem]">
          {renderItems(items, collapsed, expand)}
        </ul>
      </nav>

      {(footerItems.length > 0 || footer) && (
        <div className="flex flex-col gap-sm px-sm">
          <hr className="border-rule-subtle" />
          {footerItems.length > 0 && (
            <ul className="flex flex-col gap-[0.2rem]">
              {renderItems(footerItems, collapsed, expand)}
            </ul>
          )}
          {footer}
        </div>
      )}
    </div>
  );
}

export default SideNav;
